/**
 * Client service for API operations
 *
 * @author Claude
 */
import { createApiClient } from './api';
import { Client, ClientFormValues, ClientListResponse } from '../types/client';

/**
 * Map API client response to TypeScript Client type
 */
const mapApiClientToClient = (apiClient: any): Client => {
  // Handle both Sequelize instance and plain object
  const clientData = apiClient.dataValues || apiClient;

  // Extract latestReportDate from expanded Reports array if available
  let latestReportDate: string | undefined;
  if (clientData.Reports && Array.isArray(clientData.Reports) && clientData.Reports.length > 0) {
    const sorted = [...clientData.Reports].sort(
      (a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
    latestReportDate = sorted[0].updatedAt;
  }

  return {
    id: clientData.clientId || clientData.id,
    name: clientData.name,
    description: clientData.description,
    contactName: clientData.contactName,
    contactTitle: clientData.contactTitle,
    contactEmail: clientData.contactEmail,
    contactPhone: clientData.contactPhone,
    sharepointLink: clientData.sharepointLink,
    logoUrl: clientData.logoPath
      ? `/clients/${clientData.clientId || clientData.id}/logo`
      : undefined,
    createdAt: clientData.createdAt,
    updatedAt: clientData.updatedAt,
    ...(latestReportDate && { latestReportDate }),
  };
};

/**
 * Get all clients with pagination, sorting, and search
 */
export const getClients = async (
  token: string,
  limit = 100,
  offset = 0,
  order = 'name',
  dir = 'asc',
  search?: string,
  filters?: Record<string, any>
): Promise<ClientListResponse> => {
  const api = createApiClient(token);

  // Build query parameters
  const params = new URLSearchParams({
    limit: limit.toString(),
    offset: offset.toString(),
    order,
    dir,
  });

  // Add search query if provided
  if (search) {
    params.append('q', search);
  }

  // Add additional filters
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, value.toString());
      }
    });
  }

  const response = await api.get<any>(`/clients?${params.toString()}`);

  // Map the clients array to fix the id field
  const mappedClients = response.data.clients.map(mapApiClientToClient);

  return {
    clients: mappedClients,
    total: response.data.total,
    limit: response.data.limit,
    offset: response.data.offset,
  };
};

/**
 * Get a client by ID
 */
export const getClientById = async (token: string, clientId: string): Promise<Client> => {
  const api = createApiClient(token);
  const response = await api.get<any>(`/clients/${clientId}`);
  return mapApiClientToClient(response.data);
};

/**
 * Create a new client
 */
export const createClient = async (token: string, client: ClientFormValues): Promise<Client> => {
  const api = createApiClient(token);
  const response = await api.post<any>('/clients', client);
  return mapApiClientToClient(response.data);
};

/**
 * Update an existing client
 */
export const updateClient = async (
  token: string,
  clientId: string,
  client: ClientFormValues
): Promise<Client> => {
  const api = createApiClient(token);
  const response = await api.put<any>(`/clients/${clientId}`, client);
  return mapApiClientToClient(response.data);
};

/**
 * Delete a client
 */
export const deleteClient = async (token: string, clientId: string): Promise<void> => {
  const api = createApiClient(token);
  await api.delete(`/clients/${clientId}`);
};

/**
 * Upload a logo for a client
 */
export const uploadClientLogo = async (
  token: string,
  clientId: string,
  file: File
): Promise<{ logoUrl: string }> => {
  const api = createApiClient(token);
  const formData = new FormData();
  formData.append('logo', file);
  const response = await api.post<any>(`/clients/${clientId}/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

/**
 * Delete a client's logo
 */
export const deleteClientLogo = async (token: string, clientId: string): Promise<void> => {
  const api = createApiClient(token);
  await api.delete(`/clients/${clientId}/logo`);
};

/**
 * Alias for getClientById for consistency with hooks
 */
export const getClient = getClientById;
